// src/components/auth/Login.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, signOut, browserPopupRedirectResolver } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../firebaseConfig';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import { FcGoogle } from 'react-icons/fc';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  // --- Handlers ---
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    if (!email || !password) {
      setError('Please enter both email and password.');
      setLoading(false);
      return;
    }
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/');
    } catch (err) {
      setError('Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setGoogleLoading(true);
    // Prevent App.js from processing auth state during login check
    sessionStorage.setItem('login_in_progress', '1');
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      const result = await signInWithPopup(auth, provider, browserPopupRedirectResolver);

      // Check if user has been approved (has Firestore users doc)
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      sessionStorage.removeItem('login_in_progress');

      if (userDoc.exists()) {
        window.location.href = '/dashboard';
      } else {
        // No account here — accounts are created by the owner
        await signOut(auth);
        setError('No account found for this Google account.');
      }
    } catch (err) {
      sessionStorage.removeItem('login_in_progress');
      if (err.code !== 'auth/popup-closed-by-user' && err.code !== 'auth/cancelled-popup-request') {
        setError('Google sign-in failed. Please try again.');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  // --- End Handlers ---

  return (
    <div className="flex flex-col items-center justify-center py-20 px-4 sm:px-6 lg:px-8 min-h-[80vh]">
        <div className="max-w-md w-full space-y-8 bg-warm-gray/10 backdrop-blur-xl rounded-[2.5rem] p-10 border border-white/5 shadow-2xl">
          {/* Card Header */}
          <div className="text-center">
            <h2 className="text-4xl font-black text-cream uppercase tracking-tighter drop-shadow-lg mb-2">
              Welcome <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-blue to-cream">Back</span>
            </h2>
            <p className="text-cream/50 font-medium text-lg">
              Sign in to access your content strategy
            </p>
          </div>

          {/* Google Sign-In */}
          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={loading || googleLoading}
            className="w-full flex items-center justify-center gap-3 py-4 px-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl text-sm font-bold text-cream uppercase tracking-wider transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            <FcGoogle size={20} />
            {googleLoading ? 'Signing in...' : 'Sign in with Google'}
          </button>

          {/* Divider */}
          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-[11px] text-cream/30 uppercase tracking-widest font-bold">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Login Form */}
          <form className="space-y-6" onSubmit={handleEmailLogin}>
            <div className="space-y-1">
              <label htmlFor="email-address" className="text-xs font-bold text-cream/40 uppercase tracking-wider ml-1">Email address</label>
              <input
                id="email-address" name="email" type="email" autoComplete="email" required
                className="block w-full bg-black/20 border-transparent rounded-2xl text-cream placeholder-cream/20 focus:border-slate-blue focus:ring-0 transition-all py-4 px-5 shadow-inner"
                placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={loading || googleLoading}
              />
            </div>

            <div className="relative space-y-1">
              <label htmlFor="password" className="text-xs font-bold text-cream/40 uppercase tracking-wider ml-1">Password</label>
              <div className="relative">
                <input
                    id="password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" required
                    className="block w-full bg-black/20 border-transparent rounded-2xl text-cream placeholder-cream/20 focus:border-slate-blue focus:ring-0 transition-all py-4 px-5 shadow-inner pr-10"
                    placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={loading || googleLoading}
                />
                <button
                    type="button" onClick={togglePasswordVisibility} disabled={loading || googleLoading}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-cream/40 hover:text-cream transition-colors"
                    aria-label={showPassword ? "Hide password" : "Show password"} >
                    {showPassword ? <AiOutlineEyeInvisible className="h-5 w-5" /> : <AiOutlineEye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="text-right text-sm">
              <Link to="/reset-password" className="font-semibold text-slate-blue hover:text-cream transition-colors text-xs uppercase tracking-wide">
                Forgot Password?
              </Link>
            </div>

            {error && (
              <p className="text-sm text-soft-coral bg-soft-coral/10 border border-soft-coral/20 p-3 rounded-xl text-center font-medium animate-fade-in">{error}</p>
            )}

            <div className="pt-2">
              <button
                type="submit" disabled={loading || googleLoading}
                className="w-full py-4 bg-cream text-warm-dark text-sm font-black uppercase tracking-widest rounded-full hover:bg-white hover:scale-[1.02] active:scale-[0.98] transition-all shadow-[0_0_30px_rgba(235,226,221,0.15)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </div>
          </form>

        </div>
    </div>
  );
}

export default Login;
